<template>
  <div class="station-event" v-if="event">
    <div class="d-flex align-baseline">
      <div class="headline font-weight-bold mr-8">
        <div class="d-flex align-center" v-if="event.installed_at && event.collected_at">
          <div class="mr-8">{{event.trap_no}}</div>
          <div>{{formatDate(event.installed_at)}}</div>
          <div class="mx-2">~</div>
          <div>{{formatDate(event.collected_at)}}</div>
        </div>
      </div>
      <div class="headline font-weight-bold flex">
        <div class="d-flex align-center" v-if="event.month">
          <div class="station-event__content align-center px-2">{{formatMonth(event.month)}}</div>
          <div class="ml-2">{{$t('month')}}</div>
        </div>
      </div>
    </div>
    <div class="d-flex">
      <div class="pb-3">
        {{formatDateTime(event.upload_time)}}
      </div>
    </div>
    <station-event-processing v-if="event.processing" />
    <div class="d-flex" v-else>
      <div class="pr-3 station-event-left-column">
        <div class="station-event__photo">
          <picked-insects-draw
            :imageSrc="current.original"
            :pickedDetectionType="pickedDetectionType"
            :currentPhotoKey="current.key"
            :event="event"
          >
            <loading-image :forceLoading="event.updating" :src="current.detections || current.original" />
          </picked-insects-draw>
        </div>
        <div class="mt-2 pr-1 text-right text-caption">
          {{$t('count')}}: {{photos.length}}
        </div>
        <div class="d-flex flex-nowrap">
          <div class="flex-grow-0 pr-2">
            <div
              class="thumbnail tab"
              :class="{ active: current === tab }"
              @click="selected = tab">
              <loading-image :src="tab.thumbnail" />
            </div>
          </div>
          <div class="thumbnail-slider-wrapper">
            <thumbnail-slider
              :items="photos"
              v-model="currentIndex"
              keyName="key"
            >
              <template #item="props">
                <div
                  class="thumbnail"
                  :class="{ active: currentIndex == props.index }"
                  @click="selectPhoto(props.index)"
                  v-if="props.index < processedImageCount"
                >
                  <div class="thumbnail-head">
                    <div class="thumbnail-number">{{props.index + 1}}</div>
                    <div class="thumbnail-count">{{getPhotoCount(props.item.key)}}</div>
                  </div>
                  <loading-image
                    class="thumbnail-image"
                    :forceLoading="event.updating"
                    :src="props.item.thumbnail"
                  />
                </div>
                <processing-image v-else />
              </template>
            </thumbnail-slider>
          </div>
        </div>
      </div>
      <div class="station-event__result station-event__content pa-2 station-event-right-column">
        <chart-type-select v-model="chartType" />
        <insect-count-table
          class="station-event__result-table"
          :insectCounts="event.insect_counts"
          :showRadiobutton="!event.updating"
          :pickedDetectionType.sync="pickedDetectionType"
          v-if="chartTypeIs('table')"
        />
        <insect-count-by-type-bar-chart
          :insectCounts="event.insect_counts"
          v-if="chartTypeIs('bar')"
        />
        <top-5-insect-types-pie-chart
          :insectCounts="event.insect_counts"
          v-if="chartTypeIs('pie')"
        />
      </div>
    </div>
  </div>
</template>

<i18n lang="yaml">
ja:
  title: '新着解析結果'
  count: '合計'
  month: '月分'
en:
  title: 'Recent'
  count: 'Total'
  month: ' '
</i18n>

<script>
import DateTimeFormatting from '@/mixins/dateTimeFormatting';

import ChartTypeSelect from '@/components/atoms/ChartTypeSelect';
import InsectCountByTypeBarChart from '@/components/organisms/InsectCountByTypeBarChart';
import Top5InsectTypesPieChart from '@/components/organisms/Top5InsectTypesPieChart';
import PickedInsectsDraw from '@/components/_legacy/PickedInsectsDraw';
import InsectCountTable from '@/components/InsectCountTable';
import LoadingImage from '@/components/LoadingImage';
import ProcessingImage from '@/components/ProcessingImage';
import ThumbnailSlider from '@/components/ThumbnailSlider';

import StationEventProcessing from './StationEventProcessing';

export default {
  name: 'station-v1-event',
  components: {
    ChartTypeSelect,
    InsectCountByTypeBarChart,
    InsectCountTable,
    LoadingImage,
    PickedInsectsDraw,
    ProcessingImage,
    StationEventProcessing,
    ThumbnailSlider,
    Top5InsectTypesPieChart,
  },
  mixins: [DateTimeFormatting],
  props: {
    event: {
      type: Object,
      required: true,
    },
  },
  computed: {
    current() {
      if (this.selected) {
        return this.selected;
      }
      return this.findFirstPhoto();
    },
    currentIndex() {
      if (this.selected) {
        return this.photos.findIndex(photo => this.selected === photo);
      }
      return 0;
    },
    firstUnprocessedIndex() {
      return this._.findIndex(this.photos, photo => !this.isPhotoReady(photo));
    },
    processedImageCount() {
      return this.firstUnprocessedIndex === -1 ? this.photos.length : this.firstUnprocessedIndex;
    },
    photos() {
      const { tab: _tab, ...photos } = this.event.attachments;
      const withKey = this._.map(photos, (value, key) => ({ key, ...value }));

      return withKey.sort((a, b) => a.key.localeCompare(b.key, undefined, { numeric: true }));
    },
    tab() {
      return this.event.attachments.tab || {};
    },
  },
  data() {
    return {
      selected: null,
      chartType: 'table',
      pickedDetectionType: 'all',
    };
  },
  methods: {
    isPhotoReady(photo) {
      return photo.thumbnail && photo.detections;
    },
    selectPhoto(photoIndex) {
      if (photoIndex < this.processedImageCount) {
        this.selected = this.photos[photoIndex];
      }
    },
    findFirstPhoto() {
      return (this.processedImageCount === 0 ? this.tab : this._.first(this.photos)) || {};
    },
    getPhotoCount(key) {
      const { photo_counts: counts = {} } = this.event;
      return counts[key];
    },
    chartTypeIs(view) {
      return this.chartType === view;
    },
    resetView() {
      this.chartType = 'table';
    },
  },
  watch: {
    event() {
      this.$nextTick(() => {
        this.selected = this.findFirstPhoto();
        this.pickedDetectionType = 'all';
      });
    },
  },
};
</script>

<style scoped lang="sass">
$right-column-width: 450px
$tab-image-width: 150px
$carousel-handle-width: 80px

.station-event
  &-left-column
    min-width: calc(100% - #{$right-column-width})

  &-right-column
    max-width: $right-column-width
    min-width: $right-column-width

  &__content
    border: 2px solid #ccc
    background-color: #fff

  &__photo
    overflow: auto
    text-align: center
    font-size: 0

    ::v-deep > .insect-positions-canvas
      height: 60vh

  .loading-image, .processing-image, .picked-insects-draw
    height: 100%
    min-height: 100px
    width: 100%
    object-fit: contain

.thumbnail-slider-wrapper
  min-width: 1px

.thumbnail
  height: 160px
  min-width: 240px
  max-width: 240px
  cursor: pointer
  opacity: 0.8
  border: 3px solid
  border-color: transparent

  &-head
    background-color: #666
    color: #fff
    font-size: 12px
    display: flex
    justify-content: space-between
    padding: 0 4px

  &.tab
    min-width: 160px

  &.active
    border-color: #0068B6
    opacity: 1.0

  &:not(.tab) ::v-deep img
    object-fit: cover

.col
  padding: 0
</style>
